<template>
    <div class="relative">

        <Combobox v-model="selected" @update:modelValue="onSelect" v-slot="{ open }">
            <ComboboxInput autocomplete="off" class="p-4 pl-6 rounded-4xl bg-white text-black flex items-center w-full outline-0 leading-[28px] min-h-[62px]" :class="{ 'rounded-b-none' : open && results.length }" placeholder="Search" 
            @change="query = $event.target.value"
              :displayValue="(item) => (item && item.post_title) ? item.post_title : ''" />
            <ComboboxOptions v-if="results.length" class="z-50 absolute! max-h-64 overflow-hidden top-full left-0 w-full shadow-xl rounded-b-3xl bg-[#f5f5f5] divide-y divide-dark-grey border-t border-dark-grey">
                <ComboboxOption v-for="item in results" :key="item.ID" :value="item" class="p-4 text-black grid cursor-pointer ui-active:bg-blue-light">
                    <div class="inline-flex gap-2 flex-wrap items-center">
                        <span class="font-header uppercase text-[2rem] leading-none" v-html="item.post_title"></span>
                        <span v-if="item.event_sold_out" class="bg-red text-white uppercase block px-1 text-sm">Sold Out</span>
                    </div>
                    <span class="uppercase">{{ item.event_start_date }}</span>
                </ComboboxOption>
            </ComboboxOptions>
        </Combobox>
    </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from "@headlessui/vue";

// Props kept optional for backwards-compatibility if parent passes them,
// but this component now fetches options from the API itself.
defineProps({
    items: {
        type: Array,
        required: false,
        default: () => [],
    },
    isLoading: {
        type: Boolean,
        required: false,
        default: false
    }
});

const query = ref("")
const selected = ref(null)
const results = ref([])
const loading = ref(false)
let debounceTimer = null
let currentAbortController = null

async function fetchResults(searchTerm) {

    loading.value = true;

    if (!searchTerm) {
        results.value = []
        loading.value = false
        return
    }

    // Abort any in-flight request
    if (currentAbortController) {
        currentAbortController.abort()
    }
    currentAbortController = new AbortController()
    const { signal } = currentAbortController

    loading.value = true
    try {
        const endpoint = `/wp-json/botw/v1/whats-on/?keywords=${encodeURIComponent(searchTerm)}&per_page=10`
        const response = await fetch(endpoint, { signal })
        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`)
        }
        const data = await response.json()
        // Expecting an array of items with fields like: ID, post_title, permalink, event_is_sold_out, event_start_date
        results.value = Array.isArray(data.data) ? data.data.slice(0, 10) : []
    } catch (err) {
        if (err.name !== "AbortError") {
            // Non-abort errors: clear results to be safe
            results.value = []
            // Optional: could log error to console
            // console.error(err)
        }
    } finally {
        loading.value = false
    }
}

watch(query, (newVal) => {
    // Debounce input to limit API calls
    if (debounceTimer) {
        clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
        fetchResults(newVal.trim())
    }, 250)
})

function onSelect(item) {
  if (item?.permalink) {
    window.location.href = item.permalink
  }
}
</script>
